import { useEffect, useState, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSectors } from '@/hooks/useSectors'
import { PieChart as PieChartIcon, Loader2, RefreshCw, TrendingUp, X } from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, ZAxis, ReferenceLine, Legend,
} from 'recharts'
import type { SectorTopStock } from '@/lib/types'
import PageHeader from '@/components/ui/PageHeader'

// ── Period selector ─────────────────────────────────────────────────────────

const PERIODS = [
  { id: '1d', label: '1D' },
  { id: '1w', label: '1W' },
  { id: '1m', label: '1M' },
  { id: '3m', label: '3M' },
  { id: 'ytd', label: 'YTD' },
]

// ── Helpers ─────────────────────────────────────────────────────────────────

function pct(n: number) {
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

function fmtCap(n: number): string {
  if (!n) return 'N/A'
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9) return `$${(n / 1e9).toFixed(0)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`
  return `$${n.toLocaleString()}`
}

function fmtVol(n: number): string {
  if (!n) return 'N/A'
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`
  return String(n)
}

const QUADRANT_COLORS: Record<string, string> = {
  Leading: '#22c55e',
  Improving: '#3b82f6',
  Weakening: '#f59e0b',
  Lagging: '#ef4444',
}

// ── Correlation Heatmap ─────────────────────────────────────────────────────

function CorrelationHeatmap({ labels, names, matrix }: {
  labels: string[]; names: string[]; matrix: number[][]
}) {
  const size = labels.length
  const cellSize = 52

  function corrColor(v: number): string {
    if (v >= 0.9) return 'bg-emerald-600'
    if (v >= 0.7) return 'bg-emerald-500/80'
    if (v >= 0.5) return 'bg-emerald-400/50'
    if (v >= 0.3) return 'bg-emerald-300/30'
    if (v >= 0) return 'bg-zinc-700'
    if (v >= -0.3) return 'bg-red-400/30'
    if (v >= -0.5) return 'bg-red-500/50'
    return 'bg-red-600'
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-block">
        {/* Header row */}
        <div className="flex">
          <div className="w-20 shrink-0" />
          {labels.map((l, i) => (
            <div key={l} className="text-xs text-text-muted text-center font-mono" style={{ width: cellSize }}>
              {l}
            </div>
          ))}
        </div>
        {/* Matrix rows */}
        {matrix.map((row, ri) => (
          <div key={labels[ri]} className="flex items-center">
            <div className="w-20 shrink-0 text-xs text-text-secondary truncate pr-2 text-right font-mono">
              {labels[ri]}
            </div>
            {row.map((val, ci) => (
              <div
                key={`${ri}-${ci}`}
                className={clsx(
                  'flex items-center justify-center text-xs font-mono border border-border/30 rounded-sm',
                  corrColor(val),
                )}
                style={{ width: cellSize, height: 32 }}
                title={`${names[ri]} vs ${names[ci]}: ${val.toFixed(2)}`}
              >
                {val.toFixed(2)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Top Stocks Drawer ───────────────────────────────────────────────────────

function TopStocksPanel({ stocks, sectorName, onClose }: {
  stocks: SectorTopStock[]; sectorName: string; onClose: () => void
}) {
  return (
    <div className="card p-5 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">
          Top Stocks - {sectorName}
        </h3>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary">
          <X size={16} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="table-pro w-full text-sm">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Name</th>
              <th className="!text-right">Price</th>
              <th className="!text-right">Change</th>
              <th className="!text-right">Market Cap</th>
              <th className="!text-right">Volume</th>
              <th className="!text-right">P/E</th>
            </tr>
          </thead>
          <tbody>
            {stocks.map((s) => (
              <tr key={s.symbol}>
                <td className="font-mono font-semibold text-blue-light">{s.symbol}</td>
                <td className="text-text-secondary truncate max-w-[180px]">{s.name}</td>
                <td className="text-right font-mono">${s.price?.toFixed(2) ?? 'N/A'}</td>
                <td className="text-right">
                  <span className={clsx(s.change_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg')}>
                    {pct(s.change_pct)}
                  </span>
                </td>
                <td className="text-right text-text-secondary">{fmtCap(s.market_cap)}</td>
                <td className="text-right text-text-secondary">{fmtVol(s.volume)}</td>
                <td className="text-right text-text-secondary">
                  {s.pe_ratio != null ? s.pe_ratio.toFixed(1) : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

// Lazy tab content re-homed from the former standalone /app/rrg page
const RRGTab = lazy(() => import('./RRGPage'))

export default function SectorPage() {
  const {
    performance, performanceLoading, period, setPeriod, fetchPerformance,
    rotation, rotationLoading, fetchRotation,
    topStocks, topStocksLoading, selectedSector, selectSector,
    correlation, correlationLoading, fetchCorrelation,
  } = useSectors()

  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'performance' | 'rotation' | 'correlation' | 'rrg'>(() => {
    const t = searchParams.get('tab')
    return t === 'rotation' || t === 'correlation' || t === 'rrg' ? t : 'performance'
  })

  useEffect(() => {
    fetchPerformance()
    fetchRotation()
    fetchCorrelation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRefresh = () => {
    if (activeTab === 'performance') fetchPerformance()
    else if (activeTab === 'rotation') fetchRotation()
    else if (activeTab === 'correlation') fetchCorrelation()
  }

  const isLoading = activeTab === 'performance' ? performanceLoading
    : activeTab === 'rotation' ? rotationLoading
    : activeTab === 'correlation' ? correlationLoading
    : false

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      <PageHeader
        title="Sector Analysis"
        icon={PieChartIcon}
        iconClassName="text-violet-400"
        subtitle="SPDR sector ETF rotation and relative strength"
        actions={
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn-secondary"
          >
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        }
      />

      {/* Tabs */}
      <div className="glass flex gap-1 rounded-lg p-1 w-fit">
        {(['performance', 'rotation', 'correlation', 'rrg'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              tab !== 'rrg' && 'capitalize',
              activeTab === tab
                ? 'bg-blue/10 text-blue-light'
                : 'text-text-muted hover:text-text-primary',
            )}
          >
            {tab === 'rrg' ? 'RRG' : tab}
          </button>
        ))}
      </div>

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-4">
          {/* Period toggles */}
          <div className="glass flex gap-1 rounded-lg p-1 w-fit">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  period === p.id
                    ? 'bg-blue/10 text-blue-light'
                    : 'text-text-muted hover:text-text-primary',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          {performanceLoading ? (
            <div className="skeleton h-64 rounded-xl" />
          ) : performance?.sectors?.length ? (
            <div className="card p-5">
              <h2 className="panel-title mb-4">
                Sector Returns ({PERIODS.find(p => p.id === period)?.label ?? period})
              </h2>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart
                  data={performance.sectors}
                  layout="vertical"
                  margin={{ left: 140, right: 40, top: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" tick={{ fill: '#888', fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: '#ccc', fontSize: 12 }}
                    width={130}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
                    labelStyle={{ color: '#eee' }}
                    formatter={(value: number) => [`${value.toFixed(2)}%`, 'Return']}
                  />
                  <Bar dataKey="return_pct" radius={[0, 4, 4, 0]} cursor="pointer"
                    onClick={(d) => { if (d?.etf) selectSector(d.etf) }}>
                    {performance.sectors.map((s, i) => (
                      <Cell key={i} fill={s.return_pct >= 0 ? '#22c55e' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-text-muted mt-2">Click a sector bar to view top stocks</p>
            </div>
          ) : (
            <div className="card flex items-center justify-center h-64 text-text-muted">
              No sector data available
            </div>
          )}
        </div>
      )}

      {/* Rotation Tab */}
      {activeTab === 'rotation' && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-blue-light" />
            <h2 className="panel-title">Sector Rotation Quadrant</h2>
          </div>
          <p className="text-xs text-text-muted mb-4">
            X-axis: 3-month relative strength | Y-axis: 1-month momentum.
            Sectors rotate clockwise: Improving {'->'} Leading {'->'} Weakening {'->'} Lagging.
          </p>

          {rotationLoading ? (
            <div className="skeleton h-[420px] rounded-xl" />
          ) : rotation?.sectors?.length ? (
            <>
              <ResponsiveContainer width="100%" height={420}>
                <ScatterChart margin={{ top: 20, right: 40, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    type="number"
                    dataKey="relative_strength"
                    name="3M Relative Strength"
                    tick={{ fill: '#888', fontSize: 11 }}
                    tickFormatter={(v) => `${v}%`}
                    label={{ value: '3M Relative Strength (%)', position: 'insideBottom', offset: -10, fill: '#666', fontSize: 11 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="momentum"
                    name="1M Momentum"
                    tick={{ fill: '#888', fontSize: 11 }}
                    tickFormatter={(v) => `${v}%`}
                    label={{ value: '1M Momentum (%)', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 11 }}
                  />
                  <ZAxis range={[200, 200]} />
                  <ReferenceLine y={rotation.median_momentum} stroke="#555" strokeDasharray="5 5" />
                  <ReferenceLine x={rotation.median_rs} stroke="#555" strokeDasharray="5 5" />
                  <Tooltip
                    contentStyle={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
                    formatter={(value: number, name: string) => [`${value.toFixed(2)}%`, name]}
                    labelFormatter={(_, payload) => {
                      const d = payload?.[0]?.payload
                      return d ? `${d.name} (${d.etf})` : ''
                    }}
                  />
                  <Legend />
                  {(['Leading', 'Improving', 'Weakening', 'Lagging'] as const).map((q) => (
                    <Scatter
                      key={q}
                      name={q}
                      data={rotation.sectors.filter((s) => s.quadrant === q)}
                      fill={QUADRANT_COLORS[q]}
                      cursor="pointer"
                      onClick={(d) => { if (d?.etf) selectSector(d.etf) }}
                    />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>

              {/* Summary table */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 stagger-children">
                {(['Leading', 'Improving', 'Weakening', 'Lagging'] as const).map((q) => {
                  const items = rotation.sectors.filter((s) => s.quadrant === q)
                  return (
                    <div key={q} className="card p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: QUADRANT_COLORS[q] }} />
                        <span className="text-xs font-semibold text-text-primary">{q}</span>
                      </div>
                      {items.length ? items.map((s) => (
                        <button
                          key={s.etf}
                          onClick={() => selectSector(s.etf)}
                          className="block w-full text-left text-xs text-text-secondary hover:text-blue-light py-0.5"
                        >
                          {s.etf} - {s.name}
                        </button>
                      )) : (
                        <span className="text-xs text-text-muted">None</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[420px] text-text-muted">
              No rotation data available
            </div>
          )}
        </div>
      )}

      {/* Correlation Tab */}
      {activeTab === 'correlation' && (
        <div className="card p-5">
          <h2 className="panel-title mb-4">
            Sector Correlation Matrix (6-month daily returns)
          </h2>
          {correlationLoading ? (
            <div className="skeleton h-64 rounded-xl" />
          ) : correlation ? (
            <CorrelationHeatmap
              labels={correlation.labels}
              names={correlation.names}
              matrix={correlation.matrix}
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-text-muted">
              No correlation data available
            </div>
          )}
        </div>
      )}

      {/* RRG Tab (ex-/app/rrg Relative Rotation Graph page, content unchanged) */}
      {activeTab === 'rrg' && (
        <Suspense fallback={<div className="p-8 text-text-muted">Loading RRG...</div>}>
          <RRGTab />
        </Suspense>
      )}

      {/* Top Stocks Drawer */}
      {selectedSector && topStocks && !topStocksLoading && (
        <TopStocksPanel
          stocks={topStocks.stocks}
          sectorName={`${topStocks.sector_etf} - ${topStocks.sector_name}`}
          onClose={() => useSectors.setState({ selectedSector: null, topStocks: null })}
        />
      )}
      {topStocksLoading && selectedSector && (
        <div className="flex items-center gap-2 mt-4 text-sm text-text-muted">
          <Loader2 size={14} className="animate-spin" />
          Loading top stocks for {selectedSector}...
        </div>
      )}
    </div>
  )
}
